import { Injectable, signal } from '@angular/core';
import { ConstantService } from 'src/app/shared/constant/constant.service';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private static readonly ACCESS_TOKEN_KEY = 'speetals_access_token';
  private static readonly ADMIN_TOKEN_KEY = 'speetals_admin_access_token';
  private static readonly ADMIN_USER_ID_KEY = 'speetals_admin_user_id';
  private static readonly IMPERSONATION_TOKEN_KEY = 'speetals_impersonation_token';
  private static readonly USER_ID_KEY = 'speetals_user_id';

  private memoryAccessToken: string | null = null;
  readonly impersonating = signal(
    !!sessionStorage.getItem(TokenStorageService.IMPERSONATION_TOKEN_KEY),
  );

  constructor() {
    const stored = sessionStorage.getItem(TokenStorageService.ACCESS_TOKEN_KEY);
    if (stored) {
      this.memoryAccessToken = stored;
    }
  }

  getAccessToken(): string | null {
    return (
      this.memoryAccessToken ??
      sessionStorage.getItem(TokenStorageService.ACCESS_TOKEN_KEY)
    );
  }

  setAccessToken(token: string, userId?: string): void {
    this.memoryAccessToken = token;
    sessionStorage.setItem(TokenStorageService.ACCESS_TOKEN_KEY, token);
    if (userId !== undefined) {
      sessionStorage.setItem(TokenStorageService.USER_ID_KEY, userId);
      localStorage.setItem(ConstantService.localStorageKeys.userId, userId);
    }
  }

  getUserId(): string | null {
    return (
      sessionStorage.getItem(TokenStorageService.USER_ID_KEY) ??
      localStorage.getItem(ConstantService.localStorageKeys.userId)
    );
  }

  setUserId(userId: string): void {
    sessionStorage.setItem(TokenStorageService.USER_ID_KEY, userId);
    localStorage.setItem(ConstantService.localStorageKeys.userId, userId);
  }

  clearAuth(): void {
    this.memoryAccessToken = null;
    sessionStorage.removeItem(TokenStorageService.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(TokenStorageService.ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(TokenStorageService.ADMIN_USER_ID_KEY);
    sessionStorage.removeItem(TokenStorageService.IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(TokenStorageService.USER_ID_KEY);
    localStorage.removeItem(ConstantService.localStorageKeys.userId);
    this.impersonating.set(false);
  }

  isImpersonating(): boolean {
    return this.impersonating();
  }

  startImpersonation(
    adminToken: string,
    impersonationToken: string,
    userId: string,
    adminUserId?: string,
  ): void {
    sessionStorage.setItem(TokenStorageService.ADMIN_TOKEN_KEY, adminToken);
    sessionStorage.setItem(
      TokenStorageService.IMPERSONATION_TOKEN_KEY,
      impersonationToken,
    );
    if (adminUserId) {
      sessionStorage.setItem(TokenStorageService.ADMIN_USER_ID_KEY, adminUserId);
    }
    const resolvedUserId =
      this.decodeUserIdFromToken(impersonationToken) ?? userId;
    this.setAccessToken(impersonationToken, resolvedUserId);
    this.impersonating.set(true);
  }

  private decodeUserIdFromToken(token: string): string | null {
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }
      const payload = JSON.parse(atob(payloadPart));
      return payload?.userId ? String(payload.userId) : null;
    } catch {
      return null;
    }
  }

  exitImpersonation(): boolean {
    const adminToken = sessionStorage.getItem(TokenStorageService.ADMIN_TOKEN_KEY);
    if (!adminToken) {
      return false;
    }

    const adminUserId = sessionStorage.getItem(
      TokenStorageService.ADMIN_USER_ID_KEY,
    );
    sessionStorage.removeItem(TokenStorageService.IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(TokenStorageService.ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(TokenStorageService.ADMIN_USER_ID_KEY);
    this.setAccessToken(adminToken, adminUserId ?? undefined);
    this.impersonating.set(false);
    return true;
  }

  getActiveToken(): string | null {
    const impersonationToken = sessionStorage.getItem(
      TokenStorageService.IMPERSONATION_TOKEN_KEY,
    );
    if (impersonationToken) {
      return impersonationToken;
    }
    return this.getAccessToken();
  }
}
